import React, { useState } from 'react';
import api from '@/lib/api';
import { notify } from '@/lib/notifications/engine';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sparkles, ShieldAlert, Star, Car, AlertTriangle, Lock } from 'lucide-react';
import { ScreeningBadge } from './ScreeningBadge';   // BS3-005

const scoreTone = (s) => (s >= 70 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : s >= 40 ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-rose-50 text-rose-700 border-rose-200');

function CandidateCard({ c, best, assigning, onAssign }) {
  const fb = c.feedback_summary;
  return (
    <div className={`rounded-md border p-2.5 space-y-1.5 ${best ? 'border-emerald-300 bg-emerald-50/40' : 'bg-white'}`} data-testid={`matching-candidate-${c.worker_user_id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`text-sm font-bold rounded-md border px-2 py-0.5 ${scoreTone(c.score)}`} data-testid={`matching-score-${c.worker_user_id}`}>{c.score}</span>
          <div className="min-w-0">
            <p className="text-sm font-medium truncate">{c.worker_name}</p>
            <div className="flex flex-wrap items-center gap-1 mt-0.5 text-[11px] text-muted-foreground">
              {c.preferred && <Badge className="bg-violet-600 text-white text-[10px] px-1 py-0" data-testid={`matching-preferred-${c.worker_user_id}`}><Star className="w-2.5 h-2.5 mr-0.5" />Preferred</Badge>}
              <span className="flex items-center gap-0.5"><Car className="w-3 h-3" />{c.travel_minutes}m</span>
              <span data-testid={`matching-feedback-${c.worker_user_id}`}>{fb?.status === 'AVAILABLE' ? `★ ${fb.average} (${fb.eligible_count})` : `Feedback: insufficient (${fb?.eligible_count ?? 0}/${fb?.required ?? 5})`}</span>
              {best && <Badge className="bg-emerald-600 text-white text-[10px] px-1 py-0">Best match</Badge>}
            </div>
          </div>
        </div>
        <Button size="sm" className="h-7 text-xs" disabled={assigning} onClick={() => onAssign(c.worker_user_id)} data-testid={`matching-assign-${c.worker_user_id}`}>Assign</Button>
      </div>
      {c.reasons?.length > 0 && <p className="text-[11px] text-slate-600">{c.reasons.join(' · ')}</p>}
      {c.warnings?.map((w, i) => <p key={i} className="text-[11px] text-amber-700 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{w}</p>)}
    </div>
  );
}

function BlockedRow({ b, sensitiveVisible }) {
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-2 flex items-start gap-2" data-testid={`matching-blocked-${b.worker_user_id}`}>
      <Lock className="w-3.5 h-3.5 text-slate-500 mt-0.5" />
      <div className="min-w-0 space-y-0.5">
        <p className="text-sm font-medium text-slate-700 truncate flex items-center gap-1.5">{b.worker_name} {b.screening && <ScreeningBadge state={b.screening} />}</p>
        {b.blocks.filter((bl) => !(b.screening && bl.code === 'WORKER_SCREENING_INVALID')).map((bl, i) => (
          <p key={i} className={`text-[11px] ${bl.sensitive ? 'text-rose-700 font-medium' : 'text-slate-600'}`} data-testid={`matching-block-reason-${b.worker_user_id}-${i}`}>
            {bl.sensitive && !sensitiveVisible ? 'Assignment Blocked / Contact Authorised Manager' : bl.message}
            {bl.sensitive && sensitiveVisible && bl.code !== 'ASSIGNMENT_BLOCKED' ? ` (${bl.code.replace(/_/g, ' ')})` : ''}
          </p>
        ))}
      </div>
    </div>
  );
}

// Day 13 — worker matching inside the existing Scheduling Board drawer. Suggestion-only: the coordinator assigns.
export function WorkerMatchingPanel({ shift, onAssign, assigning }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showBlocked, setShowBlocked] = useState(true);   // BS3-005: not-eligible workers (e.g. screening unfinished) stay visible by default

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/worker-matching/shifts/${shift.id}/candidates?limit=8`);
      setData(r.data?.data || null);
    } catch (e) { notify.error(e, 'MATCHING_FAILED'); }
    finally { setLoading(false); }
  };

  if (!data && !loading) {
    return (
      <Button variant="outline" size="sm" className="w-full h-8 text-xs" onClick={load} data-testid="matching-find-workers-btn">
        <Sparkles className="w-3.5 h-3.5 mr-1.5" />Find best-matched workers
      </Button>
    );
  }
  if (loading) return <p className="text-xs text-muted-foreground py-2 text-center" data-testid="matching-loading">Checking eligibility and matching…</p>;

  return (
    <div className="space-y-2" data-testid="matching-panel">
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Best-matched workers</p>
        <span className="text-[11px] text-muted-foreground" data-testid="matching-counts">{data.eligible_count} eligible · {data.blocked_count} blocked</span>
      </div>
      {data.candidates.length === 0 && <p className="text-xs text-muted-foreground" data-testid="matching-empty">No eligible workers found for this shift.</p>}
      {data.candidates.map((c, i) => <CandidateCard key={c.worker_user_id} c={c} best={i === 0} assigning={assigning} onAssign={onAssign} />)}
      {data.blocked_count > 0 && (
        <div className="flex items-center justify-between pt-1">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground" data-testid="matching-not-eligible-title">Not eligible yet ({data.blocked_count})</p>
          <button type="button" className="text-[11px] text-slate-600 underline flex items-center gap-1" onClick={() => setShowBlocked(v => !v)} data-testid="matching-toggle-blocked">
            <ShieldAlert className="w-3 h-3" />{showBlocked ? 'Hide' : 'Show'}
          </button>
        </div>
      )}
      {showBlocked && data.blocked.map(b => <BlockedRow key={b.worker_user_id} b={b} sensitiveVisible={data.sensitive_details_visible} />)}
      <p className="text-[10px] text-muted-foreground italic">{data.note}</p>
    </div>
  );
}
